import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useState } from 'react'
import { create as createNiceModal, useModal } from '@ebay/nice-modal-react'
import styled from 'styled-components'
import { defaultTheme } from 'src/theme'
import { Spinner } from 'react-bootstrap'
import { Box } from '@mui/material'
import { I18n, Translate } from 'src/i18n'
import BaseDrawer from 'src/ui-components/modals/BaseDrawer'
import { ExperianAutocheckButton } from 'src/features/customizeSearch/components/ExperianAutocheckButton'
import { VehicleDetail } from 'src/store/vehicleHistory/interfaces'
import VehicleServiceProvider from 'src/services/VehicleServiceProvider'
import { ArrowBack, Close } from '@mui/icons-material'
import { Hyperlink } from '../../../ui-components/util/Hyperlink'
import { breakpoints } from '../../../theme/breakpoints'

interface IVINDetails {
  VIN: string
  onBack?: () => void
}

export const VinDetailsDrawer = createNiceModal(
  observer((props: IVINDetails): ReactElement => {
    const { VIN, onBack } = props
    const [vehicleDetail, setVehicleDetail] = useState<VehicleDetail>()
    const [vehicleDetailLoading, setVehicleDetailLoading] = useState(false)
    const modal = useModal()

    useEffect(() => {
      getVINDetails(VIN)
    }, [VIN])

    const getVINDetails = async (vin) => {
      setVehicleDetailLoading(true)
      try {
        const data = await VehicleServiceProvider.getVehicleDetails(vin)
        setVehicleDetail(data)
      } catch (error) {
        throw new Error(JSON.stringify(error))
      } finally {
        setVehicleDetailLoading(false)
      }
    }

    const handleBackToVehicleDetails = () => {
      modal.remove()
      onBack?.()
    }

    const title = (
      <TitleSection>
        <FlexColumn>
          {onBack && (
            <StyledHyperlink
              action={handleBackToVehicleDetails}
              underlined={false}
            >
              <ArrowBack style={{ marginRight: '4px' }} />
              <I18n name="backToVehicleDetails" style={{ fontSize: '16px' }} />
            </StyledHyperlink>
          )}

          <Title>
            <I18n name="vinDetails" />
          </Title>
        </FlexColumn>
        <FlexColumn style={{ justifyContent: 'center' }}>
          <StyledClose onClick={() => modal.remove()} />
        </FlexColumn>
      </TitleSection>
    )

    const drawerHeader = (
      <>
        {vehicleDetail?.year && vehicleDetail?.make && vehicleDetail?.model && (
          <HeaderTitle>{`${vehicleDetail?.year} ${vehicleDetail?.make} ${vehicleDetail?.model}`}</HeaderTitle>
        )}

        {vehicleDetail?.engine && (
          <HeaderSubtitle>
            {vehicleDetail?.engine}{' '}
            <ExperianAutocheckButton vin={vehicleDetail?.vin} />
          </HeaderSubtitle>
        )}
      </>
    )

    const drawerBody = (
      <div>
        {vehicleDetailLoading && !vehicleDetail && (
          <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Spinner animation="border" />
          </Box>
        )}
        {vehicleDetail && (
          <TablesContainer>
            <Table>
              <Row>
                <TableHead>{Translate('VIN').toUpperCase()}</TableHead>
                <TableValue>
                  <span>
                    <GrayVIN>{vehicleDetail?.vin.substring(0, 9)}</GrayVIN>
                    {vehicleDetail?.vin.substring(9, 17)}
                  </span>
                </TableValue>
              </Row>
              <Row>
                <TableHead>{Translate('vinPattern')}</TableHead>
                <TableValue>{vehicleDetail?.vinPattern}</TableValue>
              </Row>
              <Row>
                <TableHead>{Translate('licensePlate')}</TableHead>
                <TableValue>{vehicleDetail?.plate}</TableValue>
              </Row>
              <Row>
                <TableHead>{Translate('state')}</TableHead>
                <TableValue>{vehicleDetail?.state}</TableValue>
              </Row>
            </Table>
            <Table>
              {vehicleDetail?.vehicleSpecs?.map((item, index) => (
                <Row key={index}>
                  <TableHead>{item?.label}</TableHead>
                  <TableValue>{item?.value}</TableValue>
                </Row>
              ))}
            </Table>
          </TablesContainer>
        )}
      </div>
    )

    return (
      <BaseDrawer
        open={modal.visible}
        title={title}
        header={drawerHeader}
        showHeaderDivider
        body={drawerBody}
        onClose={() => modal.remove()}
        width={window.innerWidth < 538 ? `${window.innerWidth}px` : '533px'}
        paddingX={
          window.innerWidth <= breakpoints.mobileMedium ? `16px` : '2rem'
        }
      />
    )
  })
)

const HeaderTitle = styled.span`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #000000;
  margin: 16px 0 0 0;
`
const HeaderSubtitle = styled.span`
  display: flex;
  flex-wrap: wrap;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  display: flex;
  align-items: center;
  color: #9b9b9b;
  margin-bottom: 8px;
  justify-content: space-between;

  @media screen and (width <= ${breakpoints.mobileMedium + 1}px) {
    flex-direction: column;
    align-items: start;
  }
`
const TablesContainer = styled.div`
  padding: 12px 0;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 80%;
`

const Row = styled.div`
  display: flex;
`

const Table = styled.div`
  width: 100%;
  margin-bottom: 16px;
  border-top: 0.5px solid ${defaultTheme.colors.greyBorder};
`

const GrayVIN = styled.span`
  color: ${defaultTheme.colors.columnBorder};
`

const TableHead = styled.div`
  border-left: 0.5px solid ${defaultTheme.colors.greyBorder};
  border-bottom: 0.5px solid ${defaultTheme.colors.greyBorder};
  border-right: 0.5px solid ${defaultTheme.colors.greyBorder};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 12px 16px;
  gap: 10px;
  width: 184px;
  height: auto;
  background: #fafafa;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #050505;
  @media screen and (width <= ${breakpoints.mobileMedium}px) {
    padding: 12px 8px;
  }
`
const TableValue = styled.div`
  border-bottom: 0.5px solid ${defaultTheme.colors.greyBorder};
  border-right: 0.5px solid ${defaultTheme.colors.greyBorder};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 12px 16px;
  gap: 10px;
  width: 301px;
  height: auto;
  background: #ffffff;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #050505;
  min-width: 198px;
  @media screen and (width <= ${breakpoints.mobileMedium}px) {
    padding: 12px 8px;
    min-width: 177px;
  }
`

const BaseSection = styled.div``

const TitleSection = styled(BaseSection)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledHyperlink = styled(Hyperlink)`
  font-size: 16px !important;
  align-items: center;
`

const Title = styled.span`
  font-family: 'Oswald';
  font-style: normal;
  text-transform: capitalize;

  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  /* identical to box height, or 150% */
  /* Black */

  color: #000000;
`

const StyledClose = styled(Close)`
  font-size: 30px;
  cursor: pointer;
`
